import { cn } from './cn';

/**
 * Size options for the Spinner component.
 */
export type SpinnerSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Spinner component.
 */
export interface SpinnerProps {
  /**
   * Size of the spinner.
   * @default 'md'
   */
  size?: SpinnerSize;
  /** Accessible label text for screen readers. Defaults to "Loading". */
  label?: string;
  /** Additional CSS classes for the wrapper element. */
  className?: string;
}

const sizeMap: Record<SpinnerSize, { svg: string; text: string }> = {
  sm: { svg: 'w-4 h-4', text: 'text-xs' },
  md: { svg: 'w-6 h-6', text: 'text-sm' },
  lg: { svg: 'w-10 h-10', text: 'text-base' },
};

/**
 * Animated SVG loading spinner with optional visible label text.
 * Always includes an accessible aria-label for screen readers.
 *
 * @param props.size - Spinner size (sm | md | lg)
 * @param props.label - Screen reader label, also shown as visible text when provided
 * @example
 * ```tsx
 * <Spinner />
 * <Spinner size="lg" label="Loading events..." />
 * ```
 */
export function Spinner({ size = 'md', label = 'Loading', className }: SpinnerProps) {
  const { svg, text } = sizeMap[size];

  return (
    <div
      role="status"
      aria-label={label}
      className={cn('inline-flex flex-col items-center gap-2', className)}
    >
      <svg
        className={cn('animate-spin text-[var(--color-primary,theme(colors.blue.500))]', svg)}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      {label !== 'Loading' && <span className={cn('text-ink-muted', text)}>{label}</span>}
    </div>
  );
}
